import { SVG } from '@svgdotjs/svg.js';
import {
  geoJson2SvgDrawLabels,
  geoJson2SvgDrawPaths,
} from '../utilities/geojson2svg';
import { GeoJson2SvgSetupWorkerInput } from '../workers/geojson2SvgSetup.worker';
import { useGeoJson2SvgDrawWorker } from './useGeojson2SvgDrawWorker';
import { useGeoJson2SvgSetupWorker } from './useGeojson2SvgSetupWorker';
import { useCallback, useMemo } from 'react';

export function useGeoJson2Svg() {
  const geoJson2SvgSetupWorker = useGeoJson2SvgSetupWorker();
  const geoJson2SvgDrawWorker = useGeoJson2SvgDrawWorker();

  const generate = useCallback(
    async (...args: GeoJson2SvgSetupWorkerInput) => {
      const setup = await geoJson2SvgSetupWorker.call(args);

      const svg = SVG();
      svg.width(setup.viewport.width);
      svg.height(setup.viewport.height);

      await geoJson2SvgDrawPaths(svg, setup);
      await geoJson2SvgDrawLabels(svg, setup);

      // credit: https://stackoverflow.com/a/79196371
      const img = new Image();
      img.src = URL.createObjectURL(
        new Blob([svg.svg()], { type: 'image/svg+xml' })
      );
      await img.decode();
      URL.revokeObjectURL(img.src);
      const bmp = await createImageBitmap(img);

      return await geoJson2SvgDrawWorker.call([bmp, setup]);
    },
    [geoJson2SvgDrawWorker, geoJson2SvgSetupWorker]
  );

  const loading = useMemo(
    () => geoJson2SvgSetupWorker.loading || geoJson2SvgDrawWorker.loading,
    [geoJson2SvgDrawWorker, geoJson2SvgSetupWorker]
  );

  return useMemo(
    () => ({
      generate,
      loading,
    }),
    [generate, loading]
  );
}
